/**
 * Opportunity Table — dense enterprise list view.
 */
import React from "react";
import { ArrowUpRight } from "lucide-react";
import {
  fmtAud,
  SeverityChip,
} from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

const COLS = [
  { key: "id", label: "ID", w: "w-[112px]" },
  { key: "title", label: "Opportunity", w: "" },
  { key: "department", label: "Department", w: "w-[140px] hidden md:table-cell" },
  { key: "businessUnit", label: "Business Unit", w: "w-[160px] hidden lg:table-cell" },
  { key: "status", label: "Status", w: "w-[110px] hidden md:table-cell" },
  { key: "roiScore", label: "ROI", w: "w-[64px] text-right tabular-nums" },
  { key: "riskScore", label: "Risk", w: "w-[64px] text-right tabular-nums hidden md:table-cell" },
  { key: "effortScore", label: "Effort", w: "w-[64px] text-right tabular-nums hidden md:table-cell" },
  { key: "priorityScore", label: "Priority", w: "w-[80px] text-right tabular-nums" },
  { key: "estimatedValueAud", label: "Est. value", w: "w-[110px] text-right tabular-nums" },
  { key: "paybackMonths", label: "Payback", w: "w-[90px] text-right tabular-nums hidden xl:table-cell" },
  { key: "complianceImpact", label: "Compliance", w: "w-[120px] hidden xl:table-cell" },
];

const STATUS_TONE = {
  new: "bg-tralenex-50 text-tralenex-700",
  endorsed: "bg-gold-300/10 text-gold-600",
  "in-review": "bg-inset text-ink-secondary",
  "in-flight": "bg-opportunity/10 text-opportunity",
  live: "bg-risk-low/10 text-risk-low",
};

export function OpportunityTable({ items, onSelect }) {
  return (
    <div className="surface-card shadow-e1 overflow-hidden" data-testid="radar-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-inset/60 border-b border-border-subtle">
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    "px-3 py-2 text-left text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary",
                    c.w,
                  )}
                >
                  {c.label}
                </th>
              ))}
              <th className="w-[44px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {items.map((o) => (
              <tr
                key={o.id}
                className="hover:bg-inset/40 cursor-pointer transition-colors"
                onClick={() => onSelect(o)}
                data-testid={`radar-table-row-${o.id}`}
              >
                <td className="px-3 py-2.5 font-mono text-meta text-ink-secondary tabular-nums">
                  {o.id}
                </td>
                <td className="px-3 py-2.5">
                  <p className="font-medium text-ink-primary truncate max-w-[420px]">{o.title}</p>
                  <p className="text-meta text-ink-secondary truncate max-w-[420px]">
                    {o.recommendedApproach}
                  </p>
                </td>
                <td className="px-3 py-2.5 text-ink-secondary hidden md:table-cell">{o.department}</td>
                <td className="px-3 py-2.5 text-ink-secondary hidden lg:table-cell">{o.businessUnit}</td>
                <td className="px-3 py-2.5 hidden md:table-cell">
                  <span
                    className={cn(
                      "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
                      STATUS_TONE[o.status] || "bg-inset text-ink-secondary",
                    )}
                  >
                    {o.status}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink-primary">{o.roiScore}</td>
                <td className="px-3 py-2.5 text-right tabular-nums hidden md:table-cell">
                  <span
                    className={cn(
                      o.risk === "high" ? "text-risk-high" : o.risk === "med" ? "text-risk-med" : "text-risk-low",
                    )}
                  >
                    {o.riskScore}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink-primary hidden md:table-cell">
                  {o.effortScore}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-ink-primary">
                  {o.priorityScore}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink-primary">
                  {fmtAud(o.estimatedValueAud)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink-secondary hidden xl:table-cell">
                  {o.paybackMonths} mo
                </td>
                <td className="px-3 py-2.5 hidden xl:table-cell">
                  <SeverityChip severity={o.complianceImpact} />
                </td>
                <td className="px-3 py-2.5 text-ink-disabled">
                  <ArrowUpRight size={14} />
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={COLS.length + 1} className="px-6 py-10 text-center text-ink-secondary">
                  No opportunities match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
